import { create } from 'zustand';
import * as energyAPI from '@/api/energy';
import type { EnergyStatus, EnergyConfig } from '@/api/energy';

interface EnergyState {
  // State
  energy: number;
  maxEnergy: number;
  regenRateMinutes: number;
  nextRegenAt: number;
  unlimited: boolean;
  config: EnergyConfig | null;
  loading: boolean;
  showNoEnergyModal: boolean;
  noEnergyAction: string;
  noEnergyCost: number;

  // Computed
  energyPercent: () => number;
  energyColor: () => string;
  timeToNextRegen: () => number;

  // Actions
  fetchStatus: () => Promise<void>;
  fetchConfig: () => Promise<void>;
  purchaseEnergy: (vibecoinAmount: number) => Promise<boolean>;
  optimisticSpend: (amount: number) => void;
  openNoEnergyModal: (action: string, cost: number) => void;
  closeNoEnergyModal: () => void;
  reset: () => void;
}

export const useEnergyStore = create<EnergyState>((set, get) => ({
  energy: 0,
  maxEnergy: 20,
  regenRateMinutes: 72,
  nextRegenAt: 0,
  unlimited: false,
  config: null,
  loading: false,
  showNoEnergyModal: false,
  noEnergyAction: '',
  noEnergyCost: 0,

  energyPercent: () => {
    const { energy, maxEnergy, unlimited } = get();
    if (unlimited) return 100;
    return maxEnergy > 0 ? Math.round((energy / maxEnergy) * 100) : 0;
  },

  energyColor: () => {
    const pct = get().energyPercent();
    if (pct > 50) return '#4ade80'; // green
    if (pct > 20) return '#fbbf24'; // yellow
    return '#ef4444'; // red
  },

  timeToNextRegen: () => {
    const { nextRegenAt } = get();
    if (!nextRegenAt) return 0;
    return Math.max(0, nextRegenAt - Date.now());
  },

  fetchStatus: async () => {
    try {
      const status = await energyAPI.getEnergyStatus();
      set({
        energy: status.currentEnergy,
        maxEnergy: status.maxEnergy,
        regenRateMinutes: status.regenRateMinutes,
        nextRegenAt: status.nextRegenAt,
        unlimited: status.unlimited,
      });
    } catch (err) {
      console.error('[Energy] fetchStatus error:', err);
    }
  },

  fetchConfig: async () => {
    try {
      const config = await energyAPI.getEnergyConfig();
      set({ config });
    } catch (err) {
      console.error('[Energy] fetchConfig error:', err);
    }
  },

  purchaseEnergy: async (vibecoinAmount: number) => {
    try {
      const result = await energyAPI.purchaseEnergy(vibecoinAmount);
      set({ energy: result.newEnergyBalance });
      return true;
    } catch (err) {
      console.error('[Energy] purchase error:', err);
      return false;
    }
  },

  optimisticSpend: (amount: number) => {
    const { energy, unlimited } = get();
    if (unlimited) return;
    set({ energy: Math.max(0, energy - amount) });
  },

  openNoEnergyModal: (action: string, cost: number) => {
    set({ showNoEnergyModal: true, noEnergyAction: action, noEnergyCost: cost });
  },

  closeNoEnergyModal: () => {
    set({ showNoEnergyModal: false, noEnergyAction: '', noEnergyCost: 0 });
  },

  reset: () => set({
    energy: 0, maxEnergy: 20, regenRateMinutes: 72, nextRegenAt: 0,
    unlimited: false, config: null, loading: false,
    showNoEnergyModal: false, noEnergyAction: '', noEnergyCost: 0,
  }),
}));
